import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

export default function Privacy() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 bg-white">
        <div className="w-full px-6 md:px-10 lg:px-14 pt-28 md:pt-36 pb-20 md:pb-28">
          <div className="max-w-3xl mx-auto">
            <span className="inline-block text-sm font-medium text-[#4CAF50] border border-[#4CAF50]/30 rounded-full px-4 py-1.5 mb-5">
              Legal
            </span>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-8">
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-500 mb-10">
              Last updated: May 3, 2026
            </p>

            <div className="space-y-10 text-gray-700 text-sm md:text-base leading-relaxed">
              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  1. Introduction
                </h2>
                <p>
                  This Privacy Policy describes how the <strong>ScanDoc</strong> application
                  ("the Application", "we", "our") collects, uses, and shares user information.
                  By using ScanDoc, you agree to the practices described in this policy.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  2. No User Account
                </h2>
                <p>
                  ScanDoc does not require the creation of a user account. No personally
                  identifiable information (name, email address, phone number) is collected
                  by the Application.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  3. Data Collected by the Application
                </h2>

                <h3 className="text-base font-semibold text-gray-800 mb-2 mt-5">
                  3.1 Strictly Local Data
                </h3>
                <p className="mb-3">
                  The following data is stored <strong>only on your device</strong> and is
                  never transmitted to external servers:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Scanned documents and images</strong>: stored in the device's local storage
                  </li>
                  <li>
                    <strong>Document metadata</strong> (names, dates, tags): stored in a local SQLite database
                  </li>
                  <li>
                    <strong>User preferences</strong> (theme, sort mode, favorites): stored in local SharedPreferences
                  </li>
                  <li>
                    <strong>Signature and watermark settings</strong>: stored locally
                  </li>
                  <li>
                    <strong>PIN code and biometric data</strong>: stored locally via SharedPreferences
                  </li>
                </ul>

                <h3 className="text-base font-semibold text-gray-800 mb-2 mt-5">
                  3.2 Data Collected via Third-Party SDKs
                </h3>
                <p className="mb-3">
                  The Application uses third-party SDKs that automatically collect certain data:
                </p>

                <div className="space-y-4 mt-4">
                  <div className="border-l-4 border-gray-200 pl-4">
                    <h4 className="font-semibold text-gray-800">a) Google AdMob (Advertising)</h4>
                    <ul className="list-disc pl-6 space-y-1 mt-1">
                      <li><strong>Data collected:</strong> Google Advertising ID, IP address, device type, operating system version</li>
                      <li><strong>Purpose:</strong> Displaying advertisements</li>
                      <li><strong>Recipient:</strong> Google LLC</li>
                      <li><strong>Policy:</strong> <a href="https://policies.google.com/privacy" className="text-[#4A90D9] hover:underline" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-gray-200 pl-4">
                    <h4 className="font-semibold text-gray-800">b) Facebook Audience Network (Advertising)</h4>
                    <ul className="list-disc pl-6 space-y-1 mt-1">
                      <li><strong>Data collected:</strong> Advertising ID, IP address, device type, network information</li>
                      <li><strong>Purpose:</strong> Displaying advertisements</li>
                      <li><strong>Recipient:</strong> Meta Platforms, Inc.</li>
                      <li><strong>Policy:</strong> <a href="https://www.facebook.com/about/privacy" className="text-[#4A90D9] hover:underline" rel="noopener noreferrer">https://www.facebook.com/about/privacy</a></li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-gray-200 pl-4">
                    <h4 className="font-semibold text-gray-800">c) Firebase Crashlytics (Stability)</h4>
                    <ul className="list-disc pl-6 space-y-1 mt-1">
                      <li><strong>Data collected:</strong> Installation UUID, device model, operating system version, crash traces</li>
                      <li><strong>Purpose:</strong> Analyzing and fixing Application crashes</li>
                      <li><strong>Recipient:</strong> Google LLC (Firebase)</li>
                      <li><strong>Policy:</strong> <a href="https://firebase.google.com/support/privacy" className="text-[#4A90D9] hover:underline" rel="noopener noreferrer">https://firebase.google.com/support/privacy</a></li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-gray-200 pl-4">
                    <h4 className="font-semibold text-gray-800">d) Firebase Analytics (Analytics)</h4>
                    <ul className="list-disc pl-6 space-y-1 mt-1">
                      <li><strong>Data collected:</strong> Installation ID, Application usage events (first open, sessions, screens visited), device type</li>
                      <li><strong>Purpose:</strong> Audience analysis and Application improvement</li>
                      <li><strong>Recipient:</strong> Google LLC (Firebase)</li>
                      <li><strong>Policy:</strong> <a href="https://firebase.google.com/support/privacy" className="text-[#4A90D9] hover:underline" rel="noopener noreferrer">https://firebase.google.com/support/privacy</a></li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-gray-200 pl-4">
                    <h4 className="font-semibold text-gray-800">e) Google Play Billing (In-App Purchases)</h4>
                    <ul className="list-disc pl-6 space-y-1 mt-1">
                      <li><strong>Data collected:</strong> Purchase tokens, product identifiers</li>
                      <li><strong>Purpose:</strong> In-app subscription management</li>
                      <li><strong>Recipient:</strong> Google LLC</li>
                      <li><strong>Policy:</strong> <a href="https://policies.google.com/privacy" className="text-[#4A90D9] hover:underline" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  4. Methods of Collection
                </h2>
                <p className="mb-3">
                  Data is collected automatically by the third-party SDKs integrated into the
                  Application. The Application itself does not actively collect personal data.
                  The SDKs collect data via:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The Android Advertising ID</li>
                  <li>Information about the device and operating system</li>
                  <li>Crash logs and application events</li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  5. Data Storage and Transfer
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Local data:</strong> stored exclusively on the device in a SQLite
                    database and SharedPreferences
                  </li>
                  <li>
                    <strong>Transmitted data:</strong> sent to Google servers (AdMob, Firebase)
                    and Meta servers (Facebook Audience Network) via encrypted HTTPS connections
                  </li>
                  <li>
                    <strong>No data is sent to servers controlled by the developer</strong>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  6. Data Sharing
                </h2>
                <p className="mb-3">
                  User data is shared with the following third parties:
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 pr-4 font-semibold text-gray-800">Partner</th>
                        <th className="text-left py-2 pr-4 font-semibold text-gray-800">Data Shared</th>
                        <th className="text-left py-2 font-semibold text-gray-800">Purpose</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 pr-4">Google LLC</td>
                        <td className="py-2 pr-4">Advertising ID, IP, device type</td>
                        <td className="py-2">AdMob advertising</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 pr-4">Meta Platforms Inc.</td>
                        <td className="py-2 pr-4">Advertising ID, IP, device type</td>
                        <td className="py-2">Audience Network advertising</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4">Google Firebase</td>
                        <td className="py-2 pr-4">Installation UUID, crash data</td>
                        <td className="py-2">Analytics and crash reporting</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-3">
                  No personally identifiable information (name, email, phone number) is shared.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  7. Data Security
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    All data transmitted to third-party servers is sent via
                    <strong> HTTPS/TLS encrypted</strong> connections
                  </li>
                  <li>
                    Local data is protected by the Android device's file system
                  </li>
                  <li>
                    The Application does not have a mechanism allowing users to opt out of
                    data collection (no advertising consent banner is implemented)
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  8. Data Retention and Deletion
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Local data:</strong> can be deleted by the user through uninstalling
                    the Application or manually deleting files/documents
                  </li>
                  <li>
                    <strong>Third-party data:</strong> retention is managed by the respective privacy
                    policies of Google and Meta
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  9. Children's Privacy
                </h2>
                <p>
                  The Application is not intended for use by children under the age of 13.
                  We do not knowingly collect personal information from children under 13.
                  If we learn we have collected such information, we will delete it as soon as possible.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  10. Changes to This Policy
                </h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of
                  any changes by posting the new Privacy Policy on this page and updating
                  the "Last updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-gray-900 mb-3">
                  11. Contact Us
                </h2>
                <p>
                  If you have any questions about this Privacy Policy, please contact us at{" "}
                  <a
                    href="mailto:scandoc@forcekeys.com"
                    className="text-[#4A90D9] hover:underline"
                  >
                    scandoc@forcekeys.com
                  </a>
                  .
                </p>
              </section>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}